import { useState, useEffect } from 'react';
import { useFrameworkVersions, useFrameworkVersion, useRestoreFrameworkVersion, type FrameworkVersionListItem } from '@/hooks/useFrameworkChanges';
import { useEditMode } from '@/contexts/EditModeContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';
import { RotateCcw } from 'lucide-react';

export function FrameworkHistoryTab({ onSwitchToFramework }: { onSwitchToFramework: () => void }) {
  const { data, isLoading } = useFrameworkVersions();
  const { enterEditMode } = useEditMode();
  const restoreVersion = useRestoreFrameworkVersion();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const versions = data?.versions || [];
  const currentVersion = versions[0]; // newest first

  // Auto-select first version
  useEffect(() => {
    if (versions.length > 0 && !versions.some(v => v.id === selectedVersionId)) {
      setSelectedVersionId(versions[0].id);
    }
  }, [versions, selectedVersionId]);

  const handleRestore = async (v: FrameworkVersionListItem) => {
    if (!confirm(
      `Restore framework to v${v.versionNumber}?\n\n` +
      `This will create a new pending change set containing all the changes needed to revert. ` +
      `The change set must be approved like any other — the framework is NOT immediately changed.`
    )) return;

    try {
      const res = await restoreVersion.mutateAsync(v.id);
      // Drop the user into Edit Mode for the auto-generated change set
      enterEditMode(res.changeSetId);
      onSwitchToFramework();
      alert(
        `Restore change set created with ${res.itemCount} item(s). ` +
        `You're now editing it — review the changes and click Submit for review when ready.`
      );
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed to restore: ${err.message || 'unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium text-gray-900">Services Framework version history</h3>
        <p className="text-xs text-gray-500 mt-1">
          Every approved change set creates an immutable Services Framework version. Restore a previous
          version to roll back — the restore goes through the normal approval workflow.
        </p>
      </div>

      <div className="flex gap-4 h-[calc(100vh-300px)] min-h-[400px]">
        {/* Version list */}
        <div className="w-80 flex-shrink-0 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h4 className="font-semibold text-gray-900 text-sm">
              Versions ({versions.length})
            </h4>
          </div>
          <div className="flex-1 overflow-y-auto">
            {versions.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">No versions yet</div>
            ) : (
              versions.map((v) => {
                const isCurrent = v.id === currentVersion?.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVersionId(v.id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 border-b border-gray-100 transition-colors',
                      selectedVersionId === v.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-mono font-bold text-xtravirt-navy">
                        v{v.versionNumber}
                      </span>
                      {isCurrent && (
                        <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-700 truncate mt-0.5">
                      {v.changeSetTitle || v.notes || '(no title)'}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {new Date(v.appliedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {' · '}{v.appliedBy}
                    </div>
                    {v.itemCount > 0 && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {v.itemCount} change{v.itemCount === 1 ? '' : 's'}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div className="flex-1 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
          {selectedVersionId ? (
            <FrameworkVersionDetailView
              versionId={selectedVersionId}
              isCurrent={selectedVersionId === currentVersion?.id}
              onRestore={handleRestore}
              isRestoring={restoreVersion.isPending}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Select a version to view its snapshot
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FrameworkVersionDetailView({
  versionId,
  isCurrent,
  onRestore,
  isRestoring,
}: {
  versionId: string;
  isCurrent: boolean;
  onRestore: (v: FrameworkVersionListItem) => void;
  isRestoring: boolean;
}) {
  const { data, isLoading } = useFrameworkVersion(versionId);

  if (isLoading || !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const { version, snapshot } = data;
  const snapshotData = snapshot as null | {
    workstreams?: unknown[];
    capabilities?: unknown[];
    activities?: unknown[];
    dependencies?: unknown[];
    guardrails?: unknown[];
    skills?: unknown[];
    roles?: unknown[];
    generalMultipliers?: unknown[];
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">
              v{version.versionNumber}
            </h3>
            {isCurrent && (
              <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">
                Current
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Applied {new Date(version.appliedAt).toLocaleString('en-GB')} by {version.appliedBy}
          </p>
          {version.changeSetTitle && (
            <p className="text-sm text-gray-700 mt-2 font-medium">{version.changeSetTitle}</p>
          )}
          {version.changeSetDescription && (
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{version.changeSetDescription}</p>
          )}
          {version.notes && (
            <p className="text-xs text-gray-500 italic mt-2">{version.notes}</p>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Snapshot contents</h4>
          {snapshotData ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-1">
              <div>{snapshotData.workstreams?.length ?? 0} workstreams</div>
              <div>{snapshotData.capabilities?.length ?? 0} capabilities</div>
              <div>{snapshotData.activities?.length ?? 0} activities</div>
              <div>{snapshotData.dependencies?.length ?? 0} dependencies</div>
              <div>{snapshotData.guardrails?.length ?? 0} guardrails</div>
              <div>{snapshotData.skills?.length ?? 0} skills</div>
              <div>{snapshotData.roles?.length ?? 0} roles</div>
              <div>{snapshotData.generalMultipliers?.length ?? 0} general multipliers</div>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              No snapshot stored for this version (legacy versions before snapshot_json was added).
              Restore is not available.
            </div>
          )}
        </div>
      </div>

      {!isCurrent && snapshotData && (
        <div className="border-t border-gray-200 p-3 bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={() => onRestore(version)}
            disabled={isRestoring}
            className="btn-primary text-sm flex items-center gap-1"
            title="Create a pending change set that reverts the framework to this version"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {isRestoring ? 'Creating change set…' : `Restore to v${version.versionNumber}`}
          </button>
        </div>
      )}
    </>
  );
}
